/**
 * 充值记录页面：展示用户充值/购买套餐的订单记录。
 */

import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CreditCard,
  Receipt,
  ShieldCheck,
  AlertCircle,
  Package,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useOrders } from '@/hooks/useOrders'
import type { Order, OrderStatus } from '@/types/order'

/** 订单状态筛选类型 */
type OrderFilter = 'all' | OrderStatus

/** 筛选按钮配置 */
const FILTER_OPTIONS: readonly { value: OrderFilter; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'paid', label: '已支付' },
  { value: 'pending', label: '待支付' },
  { value: 'failed', label: '支付失败' },
] as const

/**
 * 根据订单状态返回徽标样式。
 * @param status 订单状态
 * @returns Tailwind className
 */
function getOrderStatusClassName(status: OrderStatus): string {
  switch (status) {
    case 'paid':
      return 'bg-emerald-500/10 text-emerald-700'
    case 'pending':
      return 'bg-amber-500/10 text-amber-700'
    case 'failed':
    case 'cancelled':
      return 'bg-rose-500/10 text-rose-700'
    case 'refunded':
      return 'bg-slate-500/10 text-slate-700'
    case 'expired':
      return 'bg-slate-500/10 text-slate-700'
    default:
      return 'bg-slate-500/10 text-slate-700'
  }
}

/**
 * 格式化日期时间
 * @param dateString ISO 日期字符串
 * @returns 格式化后的日期时间字符串
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

/**
 * 加载状态骨架屏
 * @returns 骨架屏 ReactElement
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-6">
      {/* 标题骨架 */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <div className="h-9 w-32 animate-pulse rounded bg-muted" />
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-10 w-64 animate-pulse rounded-lg bg-muted" />
      </div>

      {/* 统计卡片骨架 */}
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-2xl border bg-card p-5">
            <div className="h-4 w-16 rounded bg-muted" />
            <div className="mt-2 h-8 w-20 rounded bg-muted" />
          </div>
        ))}
      </div>

      {/* 筛选按钮骨架 */}
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 w-16 animate-pulse rounded-full bg-muted" />
        ))}
      </div>

      {/* 表格骨架 */}
      <div className="rounded-2xl border bg-card">
        <div className="border-b p-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="border-b p-4 last:border-b-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 animate-pulse rounded-xl bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-3 w-24 rounded bg-muted" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 错误状态组件 Props
 */
interface ErrorStateProps {
  message: string
  onRetry: () => void
}

/**
 * 错误状态组件
 * @param props 组件属性
 * @returns 错误状态 ReactElement
 */
function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">加载失败</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{message}</p>
      <Button onClick={onRetry} className="mt-6" variant="outline">
        重新加载
      </Button>
    </div>
  )
}

/**
 * 空状态组件
 * @returns 空状态 ReactElement
 */
function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Package className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">暂无订单记录</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        您还没有充值记录，去购买套餐开始使用吧
      </p>
      <Button asChild className="mt-6">
        <Link to="/pricing">
          <CreditCard className="mr-2 h-4 w-4" />
          去充值
        </Link>
      </Button>
    </div>
  )
}

/**
 * 订单列表项组件 Props
 */
interface OrderListItemProps {
  order: Order
}

/**
 * 订单列表项组件
 * @param props 组件属性
 * @returns 列表项 ReactElement
 */
function OrderListItem({ order }: OrderListItemProps): ReactElement {
  return (
    <div className="grid grid-cols-12 gap-3 p-4">
      <div className="col-span-12 md:col-span-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-secondary text-foreground/80">
            <Receipt className="h-4 w-4" />
          </span>
          <div className="space-y-1">
            <p className="font-medium">{order.packageName}</p>
            <p className="text-xs text-muted-foreground">
              {formatDateTime(order.createdAt)}
            </p>
            <p className="font-mono text-xs text-muted-foreground">
              {order.orderNo}
            </p>
          </div>
        </div>
      </div>

      <div className="col-span-6 md:col-span-2">
        <p className="font-semibold">¥{order.amountYuan}</p>
      </div>

      <div className="col-span-6 md:col-span-2">
        <p className="font-semibold">{order.totalCredits}</p>
        {order.bonusCredits > 0 ? (
          <p className="text-xs text-muted-foreground">
            含赠送 {order.bonusCredits}
          </p>
        ) : null}
      </div>

      <div className="col-span-6 md:col-span-2">
        <p className="text-sm text-foreground/90">
          {order.payTypeText ?? order.paymentMethodText ?? '-'}
        </p>
      </div>

      <div className="col-span-6 md:col-span-1">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-1 text-xs font-medium',
            getOrderStatusClassName(order.status)
          )}
        >
          {order.statusText}
        </span>
      </div>
    </div>
  )
}

/**
 * 充值记录页面组件。
 * @returns 页面 ReactElement
 */
export function RechargeRecordsPage(): ReactElement {
  const [filter, setFilter] = useState<OrderFilter>('all')
  const [query, setQuery] = useState<string>('')

  const {
    data: orders,
    isLoading,
    isError,
    error,
    refetch,
  } = useOrders()

  // 根据筛选条件过滤订单
  const filteredOrders: Order[] = useMemo((): Order[] => {
    if (orders === undefined) {
      return []
    }

    const normalizedQuery: string = query.trim().toLowerCase()

    return orders.filter((order: Order): boolean => {
      // 状态筛选
      const matchesStatus: boolean =
        filter === 'all' ? true : order.status === filter

      // 搜索筛选
      if (normalizedQuery.length === 0) {
        return matchesStatus
      }

      const haystack: string = `${order.packageName} ${order.payTypeText ?? ''} ${order.paymentMethodText ?? ''} ${order.orderNo}`.toLowerCase()
      const matchesQuery: boolean = haystack.includes(normalizedQuery)
      return matchesStatus && matchesQuery
    })
  }, [orders, filter, query])

  // 计算统计数据
  const { totalAmountYuan, totalCredits } = useMemo((): {
    totalAmountYuan: number
    totalCredits: number
  } => {
    if (orders === undefined) {
      return { totalAmountYuan: 0, totalCredits: 0 }
    }

    const paidOrders = orders.filter((order: Order) => order.status === 'paid')
    return {
      totalAmountYuan: paidOrders.reduce(
        (sum: number, order: Order) => sum + order.amountYuan,
        0
      ),
      totalCredits: paidOrders.reduce(
        (sum: number, order: Order) => sum + order.totalCredits,
        0
      ),
    }
  }, [orders])

  // 加载状态
  if (isLoading) {
    return <LoadingSkeleton />
  }

  // 错误状态
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : '加载订单记录失败，请重试'}
        onRetry={(): void => {
          void refetch()
        }}
      />
    )
  }

  // 空状态
  if (orders === undefined || orders.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">充值记录</h1>
          <p className="text-muted-foreground">
            查看你的充值/购买订单与到账算力。
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-80">
            <input
              value={query}
              onChange={(event): void => {
                setQuery(event.target.value)
              }}
              placeholder="搜索订单号 / 套餐 / 支付方式..."
              className={cn(
                'h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none',
                'transition-all focus:border-primary focus:ring-1 focus:ring-primary'
              )}
            />
          </div>
          <Button asChild className="w-full sm:w-auto">
            <Link to="/pricing">
              <CreditCard className="h-4 w-4" />
              去充值
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <p className="text-sm text-muted-foreground">累计充值</p>
          <p className="mt-2 text-2xl font-bold tracking-tight">
            ¥{totalAmountYuan.toFixed(2)}
          </p>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <p className="text-sm text-muted-foreground">累计到账算力</p>
          <p className="mt-2 text-2xl font-bold tracking-tight">{totalCredits}</p>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">交易安全</p>
              <p className="mt-2 text-sm text-foreground/90">
                采用官方支付渠道，支持订单对账与售后工单。
              </p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTER_OPTIONS.map(({ value, label }) => {
          const isActive: boolean = value === filter
          return (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={isActive ? 'default' : 'secondary'}
              className={cn(
                'rounded-full',
                isActive ? 'shadow-sm' : 'bg-secondary/70 hover:bg-secondary'
              )}
              onClick={(): void => {
                setFilter(value)
              }}
            >
              {label}
            </Button>
          )
        })}
      </div>

      <div className="rounded-2xl border bg-card shadow-sm">
        <div className="grid grid-cols-12 gap-3 border-b p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-12 md:col-span-5">订单</div>
          <div className="col-span-6 md:col-span-2">金额</div>
          <div className="col-span-6 md:col-span-2">算力</div>
          <div className="col-span-6 md:col-span-2">支付方式</div>
          <div className="col-span-6 md:col-span-1">状态</div>
        </div>

        <div className="divide-y">
          {filteredOrders.map((order: Order) => (
            <OrderListItem key={order.id} order={order} />
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="rounded-2xl border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">暂无匹配订单。</p>
          <div className="mt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={(): void => {
                setFilter('all')
                setQuery('')
              }}
            >
              清空筛选
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
